// How the pairing screen runs for real: relay HTTP + WebSocket. The mock runner (timed steps, a fixed code) lives in
// src/mock/boot.ts so it stays out of production builds.
import type { PairInfoResponse, PairLink } from "@whistle/protocol";
import { checkCryptoSupport, type DeviceIdentity } from "../lib/identity";
import { checkRelayAllowed, fetchPairInfo, joinPairing, PairError, pairTarget, parsePairLink, waitForPaired, type FetchLike } from "../lib/pairing";
import type { PairRunner } from "../screens/Pair";
import type { Controller } from "./controller";

export function realPairRunner(controller: Controller, deps: { fetchImpl?: FetchLike; WebSocketImpl?: typeof WebSocket; relays?: readonly string[] | null } = {}): (hash: string) => PairRunner {
  return (hash) => {
    let link: PairLink | null = null;
    let info: PairInfoResponse | null = null;
    let identity: DeviceIdentity | null = null;
    let deviceName = "";
    return {
      async check() {
        link = parsePairLink(hash);
        checkRelayAllowed(link.r, deps.relays);
        const support = await checkCryptoSupport();
        if (!support.ok) throw new Error(support.reason);
        info = await fetchPairInfo(link, deps.fetchImpl);
        return pairTarget(link, info);
      },
      async join(name) {
        if (!link || !info) throw new PairError("bad-link", "Pairing didn't start properly. Scan the code again.");
        deviceName = name;
        identity = await controller.newPairingIdentity();
        return joinPairing(link, info, identity, name, deps.fetchImpl);
      },
      async wait(signal) {
        if (!link || !info || !identity) throw new PairError("bad-link", "Pairing didn't start properly. Scan the code again.");
        return waitForPaired(link, info, identity, deviceName, { signal, WebSocketImpl: deps.WebSocketImpl });
      },
    };
  };
}
